import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import {
  WINE_TRAINING_PAIRING_GUIDE_PATH,
  WINE_TRAINING_PILLAR_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";

const startFreeHref = "https://app.speakyourmenu.com/signup";
const canonicalUrl = `https://speakyourmenu.com${WINE_TRAINING_PILLAR_PATH}`;
const pairingGuideUrl = `https://speakyourmenu.com${WINE_TRAINING_PAIRING_GUIDE_PATH}`;
const pageTitle = "Restaurant Wine Training Guide | Speak Your Menu";
const pageDescription = "A practical wine training guide for restaurants that need stronger server confidence, better bottle recommendations, and more consistent beverage sales conversations.";

const jumpLinks = [
  { href: "#what-it-is", label: "What it is" },
  { href: "#why-wine-training-fails", label: "Why it fails" },
  { href: "#system", label: "The system" },
  { href: "#sales-rhythm", label: "Sales rhythm" },
  { href: "#mistakes", label: "Common mistakes" },
  { href: "#faq", label: "FAQ" },
];

const keyTakeaways = [
  "Wine training works better when staff practice guest-facing language, not just grape facts or bottle trivia.",
  "The strongest system improves recommendation confidence, pairing quality, and beverage sales consistency at the same time.",
  "Managers need a repeatable way to reinforce wine knowledge after onboarding or staff will default to silence, vague descriptions, or the same safe pours.",
];

const systemSteps = [
  {
    title: "Build the list around guest language",
    body: "Start with how servers should describe each wine at the table: style, structure, guest fit, and when to recommend it.",
  },
  {
    title: "Teach recommendation patterns",
    body: "Staff need more than facts. They need short recommendation patterns for by-the-glass pours, bottle moments, and common pairing questions.",
  },
  {
    title: "Reinforce what stalls sales",
    body: "The real gaps usually show up when staff hesitate on pairings, default to one familiar label, or avoid the wine conversation entirely.",
  },
  {
    title: "Coach with commercial visibility",
    body: "Managers should know where confidence is thin so they can reinforce the right wines, price points, and pairings before service exposes the gap.",
  },
];

const salesRhythm = [
  "Day 1: Define the guest-facing language for your core pours, top bottle recommendations, and pairing anchors across the menu.",
  "Day 2: Train staff on a short wine-description framework they can repeat at the table without sounding scripted or overly technical.",
  "Day 3: Reinforce the wines that matter most commercially, including high-margin glass pours and the bottles managers want confidently recommended.",
  "Day 4: Practice pairing language tied to your most-ordered dishes so recommendations sound natural instead of improvised.",
  "Day 5: Review where staff still hesitate, over-explain, or avoid wine conversations, then coach those gaps directly.",
  "Day 6: Use pre-shift reinforcement to revisit price-point transitions, bottle upsell moments, and confidence gaps by role.",
  "Day 7: Turn the strongest scripts, pairings, and reinforcement habits into a repeatable weekly wine-training rhythm.",
];

const mistakes = [
  "Teaching wine like a certification course instead of a guest-conversation skill staff need during service.",
  "Assuming product knowledge alone will create sales confidence without practiced recommendation language.",
  "Relying on one beverage expert while the rest of the floor stays vague, passive, or silent about the list.",
  "Ignoring pairings and price transitions, which are where many bottle recommendations actually stall.",
];

const faqs = [
  {
    question: "What should restaurant wine training cover first?",
    answer: "Start with the wines staff need to recommend most often, how to describe them clearly, and which dishes they pair with naturally. Confidence comes faster when the training matches real table conversations.",
  },
  {
    question: "Do servers need deep wine theory to sell wine well?",
    answer: "No. They need useful language, a clear sense of guest fit, and enough repetition to make recommendations without freezing. That is usually more valuable than broad theory alone.",
  },
  {
    question: "Why do restaurants miss wine sales even with a strong list?",
    answer: "Because the sales gap often sits in staff confidence, not in the list itself. If servers hesitate or cannot connect wines to guest needs, the program underperforms no matter how strong the selection is.",
  },
];

export default function WineTrainingPage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Restaurant Wine Training Guide", item: canonicalUrl },
    ],
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Restaurant Wine Training Guide: A Practical System for Stronger Recommendations and Better Beverage Sales",
    description: pageDescription,
    author: { "@type": "Organization", name: "Speak Your Menu" },
    publisher: { "@type": "Organization", name: "Speak Your Menu" },
    mainEntityOfPage: canonicalUrl,
    dateModified: new Date().toISOString(),
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="homepage-redesign resource-page-shell">
        <section className="homepage-section homepage-hero-section resource-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell">
            <div className="resource-hero-copy">
              <span className="homepage-label-chip">Wine Training Systems</span>
              <h1 className="homepage-hero-title resource-hero-title">Restaurant wine training that helps staff recommend with confidence.</h1>
              <p className="homepage-hero-subhead resource-hero-subhead">
                This guide shows restaurant operators how to build a wine-training system that improves table-side language, pairing confidence, and beverage sales consistency without turning service into a wine exam.
              </p>
              <div className="homepage-hero-actions resource-hero-actions">
                <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
                <Link to={WINE_TRAINING_PAIRING_GUIDE_PATH} className="resource-secondary-link">Open the pairing guide</Link>
              </div>
              <div className="resource-jump-card homepage-surface-card">
                <p className="resource-jump-kicker">In this guide</p>
                <div className="resource-jump-links" aria-label="Jump to sections">
                  {jumpLinks.map((link) => (
                    <a key={link.href} href={link.href}>{link.label}</a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card" id="what-it-is">
              <span className="homepage-label-chip">Key takeaways</span>
              <h2 className="homepage-section-title resource-section-title">What a strong restaurant wine-training system should do</h2>
              <ul className="resource-bullet-list">
                {keyTakeaways.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <aside className="homepage-surface-card resource-card resource-card-accent">
              <span className="homepage-label-chip">Related resource</span>
              <h2 className="homepage-card-title resource-aside-title">Need the shorter execution layer?</h2>
              <p className="homepage-card-copy">Use the pairing guide when you want table-side recommendation language your team can apply immediately.</p>
              <Link to={WINE_TRAINING_PAIRING_GUIDE_PATH} className="homepage-primary-cta resource-inline-cta">View pairing guide</Link>
            </aside>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="why-wine-training-fails">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Why wine training fails</span>
            <h2 className="homepage-section-title resource-section-title">The usual problem is not the list. It is confidence transfer.</h2>
            <p className="homepage-section-body resource-section-body">
              Many restaurants have a solid wine list and still struggle to sell it because the knowledge never becomes usable language on the floor. Staff hear bottle facts in lineup, memorize a few safe descriptions, and then default to silence when a guest asks what they should drink.
            </p>
            <p className="homepage-section-body resource-section-body">
              That is why wine revenue stays concentrated in one or two experts while the rest of the team avoids the conversation. A better system turns the list into repeatable recommendation patterns, pairing anchors, and reinforcement loops managers can actually coach.
            </p>
          </div>
        </section>

        <section className="homepage-section" id="system">
          <div className="homepage-shell">
            <span className="homepage-label-chip">The system</span>
            <h2 className="homepage-section-title resource-section-title">A practical four-part system for restaurant wine training</h2>
            <div className="resource-step-grid">
              {systemSteps.map((step, index) => (
                <article key={step.title} className="homepage-surface-card resource-step-card">
                  <div className="homepage-step-number">0{index + 1}</div>
                  <h3 className="homepage-card-title">{step.title}</h3>
                  <p className="homepage-card-copy">{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="sales-rhythm">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Sales rhythm</span>
            <h2 className="homepage-section-title resource-section-title">How to turn wine knowledge into a repeatable weekly sales rhythm</h2>
            <ol className="resource-numbered-list">
              {salesRhythm.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </div>
        </section>

        <section className="homepage-section" id="mistakes">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card">
              <span className="homepage-label-chip">Common mistakes</span>
              <h2 className="homepage-section-title resource-section-title">Four mistakes that keep wine training from producing sales confidence</h2>
              <ul className="resource-bullet-list">
                {mistakes.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="homepage-surface-card resource-card resource-card-contrast">
              <span className="homepage-label-chip">Next action</span>
              <h2 className="homepage-card-title resource-aside-title">Turn this into pairing language your staff can use tonight</h2>
              <p className="homepage-card-copy">If you want the shorter operating layer, use the pairing guide to give staff clear recommendation patterns and food-match language.</p>
              <Link to={WINE_TRAINING_PAIRING_GUIDE_PATH} className="resource-secondary-link resource-secondary-link-inline">Go to pairing guide</Link>
              <a href={pairingGuideUrl} className="resource-tertiary-link">Share the pairing guide URL</a>
            </article>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="faq">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">FAQ</span>
            <h2 className="homepage-section-title resource-section-title">Questions operators ask before rebuilding wine training</h2>
            <div className="resource-faq-stack">
              {faqs.map((item) => (
                <article key={item.question} className="homepage-surface-card resource-faq-card">
                  <h3 className="homepage-card-title">{item.question}</h3>
                  <p className="homepage-card-copy">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell homepage-final-cta-shell resource-final-shell">
            <div>
              <span className="homepage-label-chip">Ready to apply it?</span>
              <h2 className="homepage-section-title resource-section-title">See how Speak Your Menu turns better wine training into daily sales confidence.</h2>
              <p className="homepage-section-body resource-section-body">
                The platform helps restaurants reinforce wine language, track weak spots, and coach beverage confidence before missed recommendations become lost revenue.
              </p>
            </div>
            <div className="resource-final-actions">
              <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
              <Link to={WINE_TRAINING_PAIRING_GUIDE_PATH} className="resource-secondary-link">Open pairing guide</Link>
              <a href={pairingGuideUrl} className="resource-tertiary-link">Share the pairing guide URL</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}